import React, { useState } from 'react';
import { Box, Grid, Typography, Chip } from '@mui/material';
import featureImage1 from '../../assets/invoice.jpg';
import featureImage2 from '../../assets/Multi-practice.jpg';
import featureImage3 from '../../assets/Invoicehistory.jpg';

const FeatureSections = () => {
  const [modalImage, setModalImage] = useState(null);

  return (
    <Box component="section" sx={{ py: 6, px: 2 }}>
      <Typography
        variant="h4"
        component="h2"
        align="center"
        sx={{ color: '#A78BFA', mb: 8, fontWeight: 'bold' }}
      >
        Key Features
      </Typography>

      {/* Feature 1: Image left, text right */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        gap: 4,
        mb: 10
      }}>
        <Box
          component="img"
          src={featureImage1}
          alt="Effortless Invoice Creation"
          sx={{
            width: { xs: '100%', md: '50%' },
            borderRadius: 2,
            border: '1px solid rgba(138, 43, 226, 0.2)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            maxHeight: { xs: 240, md: 320 },
            objectFit: 'cover',
            objectPosition: 'top',
            cursor: 'pointer' // Make image look clickable
          }}
          onClick={() => {
            // Show fullscreen modal (see below)
            setModalImage({ src: featureImage1, alt: "Effortless Invoice Creation" });
          }}
        />
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
          <Typography variant="h5" sx={{ color: '#9370DB', fontWeight: 'bold', mb: 2 }}>
            Effortless Invoice Creation
          </Typography>
          <Typography sx={{ color: 'grey.300', mb: 2 }}>
            Generate professional PDF invoices in minutes. Select the practice, add your work details, and let Coooly handle the calculations and formatting.
          </Typography>
          <Box component="ul" sx={{
            pl: 4,
            color: 'grey.400',
            '& > li': { mb: 1 },
            listStyleType: 'disc'
          }}>
            <Box component="li">Auto-calculates totals based on rates</Box>
            <Box component="li">Professional PDF templates</Box>
            <Box component="li">Easy practice selection</Box>
          </Box>
        </Box>
      </Box>

      {/* Feature 2: Text left, image right */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row-reverse' },
        alignItems: 'center',
        gap: 4,
        mb: 10
      }}>
        <Box
          component="img"
          src={featureImage2}
          alt="Manage Multiple Practices"
          sx={{
            width: { xs: '100%', md: '50%' },
            borderRadius: 2,
            border: '1px solid rgba(138, 43, 226, 0.2)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            maxHeight: { xs: 240, md: 320 },
            objectFit: 'cover',
            objectPosition: 'top',
            cursor: 'pointer'
          }}
          onClick={() => {
            setModalImage({ src: featureImage2, alt: "Manage Multiple Practices" });
          }}
        />
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
          <Typography variant="h5" sx={{ color: '#9370DB', fontWeight: 'bold', mb: 2 }}>
            Manage Multiple Practices
          </Typography>
          <Typography sx={{ color: 'grey.300', mb: 2 }}>
            Keep all your practice details organized in one place. Easily switch between practices when creating invoices, ensuring accuracy and reducing administrative hassle.
          </Typography>
          <Box component="ul" sx={{
            pl: 4,
            color: 'grey.400',
            '& > li': { mb: 1 },
            listStyleType: 'disc'
          }}>
            <Box component="li">Store addresses, contact info, and rates</Box>
            <Box component="li">Quick search and selection</Box>
            <Box component="li">Centralized practice database</Box>
          </Box>
        </Box>
      </Box>

      {/* Feature 3: Image left, text right */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        gap: 4
      }}>
        <Box
          component="img"
          src={featureImage3}
          alt="Income & Revenue Tracking"
          sx={{
            width: { xs: '100%', md: '50%' },
            borderRadius: 2,
            border: '1px solid rgba(138, 43, 226, 0.2)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            maxHeight: { xs: 240, md: 320 },
            objectFit: 'cover',
            objectPosition: 'top',
            cursor: 'pointer'
          }}
          onClick={() => {
            setModalImage({ src: featureImage3, alt: "Income & Revenue Tracking" });
          }}
        />
        <Box sx={{ width: { xs: '100%', md: '50%' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ color: '#9370DB', fontWeight: 'bold', mr: 1 }}>
              Income & Revenue Tracking
            </Typography>
            <Chip
              label="PRO"
              size="small"
              sx={{
                bgcolor: 'rgba(0, 128, 0, 0.1)',
                color: '#4ade80',
                border: '1px solid #4ade80',
                fontSize: '0.65rem',
                height: 24
              }}
            />
          </Box>
          <Typography sx={{ color: 'grey.300', mb: 2 }}>
            Gain insights into your earnings with detailed reports. Track income per practice, view payment statuses, and understand your financial performance over time.
          </Typography>
          <Box component="ul" sx={{
            pl: 4,
            color: 'grey.400',
            '& > li': { mb: 1 },
            listStyleType: 'disc'
          }}>
            <Box component="li">Visualize income trends</Box>
            <Box component="li">Filter reports by date or practice</Box>
            <Box component="li">Export data for accounting</Box>
          </Box>
        </Box>
      </Box>

      {/* Modal for fullscreen image view */}
      {modalImage && (
        <Box
          sx={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            bgcolor: 'rgba(0,0,0,0.85)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setModalImage(null)}
        >
          <Box
            sx={{
              position: 'relative',
              maxWidth: '95vw',
              maxHeight: '95vh',
              outline: 'none'
            }}
            onClick={e => e.stopPropagation()}
          >
            <img
              src={modalImage.src}
              alt={modalImage.alt}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                borderRadius: 8,
                boxShadow: '0 8px 32px rgba(0,0,0,0.7)'
              }}
            />
            <button
              onClick={() => setModalImage(null)}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 36,
                height: 36,
                fontSize: 22,
                cursor: 'pointer'
              }}
              aria-label="Close"
            >
              ×
            </button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FeatureSections;
